import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Trophy, Users, Play } from 'lucide-react';
import { WorkoutProgram } from '../../data/workoutPrograms';

interface ProgramCardProps {
  program: WorkoutProgram;
  onSelect: (id: string) => void;
}

export function ProgramCard({ program, onSelect }: ProgramCardProps) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="theme-card group relative overflow-hidden"
    >
      {/* Thumbnail */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={program.image}
          alt={program.name}
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent opacity-60" />
        
        {/* Play Button Overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="w-14 h-14 rounded-full bg-primary flex items-center justify-center"
            onClick={() => onSelect(program.id)}
          >
            <Play className="w-7 h-7" />
          </motion.button>
        </div>

        {/* Level Badge */}
        <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-primary/20 backdrop-blur-sm text-primary text-xs font-medium">
          {program.level}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        <div>
          <h3 className="font-semibold text-lg mb-1">{program.name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{program.description}</p>
        </div>

        {/* Program Details */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>{program.duration}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>{program.schedule.workoutsPerWeek}x/week</span>
          </div>
        </div>

        {/* Coach Info */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-3">
            <img
              src={program.coach.image}
              alt={program.coach.name}
              className="w-8 h-8 rounded-full object-cover"
            />
            <div>
              <div className="text-sm font-medium">{program.coach.name}</div>
              <div className="text-xs text-muted-foreground">{program.coach.specialties[0]}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-primary">
            <Users className="w-3 h-3" />
            <span>2.5k enrolled</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}